import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const F = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-emerald-600";
const L = "block text-xs font-600 uppercase tracking-wide text-zinc-500 mb-1";

export const LeadForm = ({ property }) => {
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", phone: "", country: "", whatsapp_ok: false, consent: false,
    check_in: "", check_out: "", flexible: false, adults: 2, children: 0, rooms: 1,
    room_type: "", budget: "", meal: "", occasion: "", special_requests: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) { toast.error("Please consent to be contacted by the property"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/directstay/leads", {
        property_id: property.id,
        traveler: { name: f.name, email: f.email, phone: f.phone, country: f.country, whatsapp_ok: f.whatsapp_ok, consent: f.consent },
        stay: { check_in: f.check_in, check_out: f.check_out, flexible: f.flexible, adults: Number(f.adults), children: Number(f.children), rooms: Number(f.rooms), room_type: f.room_type, budget: f.budget, meal: f.meal, occasion: f.occasion, special_requests: f.special_requests },
        source_portal: "travelerpulse", source_page: window.location.pathname,
      });
      setSent(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your inquiry");
    } finally { setBusy(false); }
  };

  if (sent) return (
    <div data-testid="lead-success" className="border-2 border-emerald-600 bg-emerald-50 p-6">
      <div className="font-heading font-900 text-lg text-emerald-800">Inquiry sent directly to {property.name}</div>
      <p className="text-sm text-emerald-700 mt-2">{sent.message}</p>
      <p className="text-xs text-emerald-600 mt-2">Lead quality: <span className="font-700 uppercase">{sent.tier.replace("_", " ")}</span></p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="lead-form" className="border border-zinc-300 bg-white p-5 space-y-4">
      <div className="font-heading font-900 text-lg">Request the best direct rate</div>
      <p className="text-xs text-zinc-500 -mt-2">Your inquiry goes straight to the property — no middleman, no commission on your stay.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={L}>Check-in *</label><input data-testid="lead-checkin" type="date" required className={F} value={f.check_in} onChange={set("check_in")} /></div>
        <div><label className={L}>Check-out *</label><input data-testid="lead-checkout" type="date" required className={F} value={f.check_out} onChange={set("check_out")} /></div>
        <div><label className={L}>Adults</label><input type="number" min="1" className={F} value={f.adults} onChange={set("adults")} /></div>
        <div><label className={L}>Children</label><input type="number" min="0" className={F} value={f.children} onChange={set("children")} /></div>
        <div><label className={L}>Rooms</label><input data-testid="lead-rooms" type="number" min="1" className={F} value={f.rooms} onChange={set("rooms")} /></div>
        <div><label className={L}>Room type</label>
          <select className={F} value={f.room_type} onChange={set("room_type")}>
            <option value="">Any</option>
            {(property.rooms || []).map((r, i) => <option key={i} value={r.name}>{r.name}</option>)}
            <option value="other">Other / not sure</option>
          </select></div>
        <div><label className={L}>Approx. budget (per night)</label><input className={F} placeholder="e.g. ₹4,000–6,000" value={f.budget} onChange={set("budget")} /></div>
        <div><label className={L}>Meal preference</label>
          <select className={F} value={f.meal} onChange={set("meal")}>
            <option value="">No preference</option><option>Breakfast included</option><option>Half board</option><option>Full board</option><option>Room only</option>
          </select></div>
        <div><label className={L}>Occasion</label>
          <select className={F} value={f.occasion} onChange={set("occasion")}>
            <option value="">—</option><option>Honeymoon</option><option>Family holiday</option><option>Wedding</option><option>Corporate</option><option>Group trip</option><option>Ayurveda / wellness</option>
          </select></div>
        <div><label className={L}>Country</label><input className={F} placeholder="India" value={f.country} onChange={set("country")} /></div>
      </div>
      <div><label className={L}>Special requests</label><textarea rows={2} className={F} value={f.special_requests} onChange={set("special_requests")} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><label className={L}>Name *</label><input data-testid="lead-name" required className={F} value={f.name} onChange={set("name")} /></div>
        <div><label className={L}>Email *</label><input data-testid="lead-email" type="email" required className={F} value={f.email} onChange={set("email")} /></div>
        <div><label className={L}>Phone / WhatsApp</label><input data-testid="lead-phone" className={F} placeholder="+91…" value={f.phone} onChange={set("phone")} /></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-zinc-600">
        <input type="checkbox" checked={f.whatsapp_ok} onChange={set("whatsapp_ok")} /> Contact me on WhatsApp
      </label>
      <label className="flex items-start gap-2 text-sm text-zinc-600">
        <input data-testid="lead-consent" type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5" />
        <span>I agree that {property.name} may contact me about this inquiry. *</span>
      </label>
      <button data-testid="lead-submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-700 py-3 text-sm uppercase tracking-wide transition-colors disabled:opacity-50">
        {busy ? "Sending…" : "Send direct inquiry"}
      </button>
    </form>
  );
};
